import { Button, ButtonColors } from '@chiroup/components/Button';
import { SupportAccessGrant } from '@chiroup/core/types/SupportAccess.type';
import dayjs from 'dayjs';
import React, { useContext } from 'react';
import { useMutation, useQuery } from 'react-query';
import { ToastContext, ToastTypes } from '../../../contexts/toast.context';
import supportAccessService from '../../../services/supportAccess.service';

type Props = {
  clinicId: number;
};

// The backend bakes the clinic-app host into launchUrl from its own deployed
// stage, so it can't know when the admin app is running locally. Re-point the
// launch URL at the host this frontend is configured for (VITE_CHIROUP_URL),
// preserving the path and token hash. Falls back to the backend URL when unset.
const resolveLaunchUrl = (launchUrl: string): string => {
  const overrideBase = import.meta.env.VITE_CHIROUP_URL as string | undefined;
  if (!overrideBase) return launchUrl;
  try {
    const target = new URL(launchUrl);
    const base = new URL(overrideBase);
    target.protocol = base.protocol;
    target.host = base.host;
    return target.toString();
  } catch {
    return launchUrl;
  }
};

const ClinicSupportAccess: React.FC<Props> = ({ clinicId }) => {
  const { createToast } = useContext(ToastContext);
  const { data, isFetching, refetch } = useQuery(
    ['supportAccessGrants', clinicId],
    () => supportAccessService.listGrants(clinicId),
    { enabled: !!clinicId, refetchOnWindowFocus: false }
  );

  const startSession = useMutation(
    (grantId: string) => supportAccessService.startSession(clinicId, grantId),
    {
      onSuccess: res => {
        window.open(resolveLaunchUrl(res.launchUrl), '_blank', 'noopener,noreferrer');
        refetch();
      },
      onError: () => {
        createToast({
          title: 'Support access failed',
          type: ToastTypes.Fail,
          description: 'Unable to start a support access session.',
          duration: 5000,
        });
      },
    }
  );

  const grants = data?.grants || [];

  return (
    <div className="m-6 rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">Support Access</h2>
        <p className="mt-1 text-sm text-gray-500">
          Start a time-boxed support session from an active customer-granted access window.
        </p>
      </div>
      <div className="p-6">
        {isFetching ? (
          <div className="text-sm text-gray-500">Loading support access grants...</div>
        ) : !grants.length ? (
          <div className="text-sm text-gray-500">No support access grants found.</div>
        ) : (
          <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Source
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Reason
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                    Expires
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {grants.map((grant: SupportAccessGrant) => {
                  const isActive = grant.status === 'active' && grant.expiresAt > Date.now();

                  return (
                    <tr key={grant.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>Acts as {grant.actAsUserName || grant.actAsUserId || 'a user'}</div>
                        {grant.caseNumber && (
                          <div className="text-xs text-gray-500">Case {grant.caseNumber}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                        {grant.reason ? (
                          <span className="italic" title={grant.reason}>
                            &ldquo;{grant.reason}&rdquo;
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm capitalize text-gray-700">{grant.status}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {dayjs(grant.expiresAt).format('MMM D, YYYY h:mm A')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          text="Start session"
                          color={ButtonColors.primary}
                          disabled={!isActive || startSession.isLoading}
                          onClick={() => startSession.mutate(grant.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicSupportAccess;
